import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { NotificationService } from "../../api/generated/services/NotificationService";
import { CustomNotificationType } from "../../api/generated/models/CustomNotificationType";
import {
  listBusinessCategories,
  BusinessCategory,
} from "../../api/businessCategories";
import { listBusinessNames, BusinessName } from "../../api/business";
import { NotificationImageVariant } from "../../api/generated/models/NotificationImageVariant";
import {
  buildSingleTransactionPayload,
  resolvePreviewImageUrl,
  imageVariantOptions,
} from "./singleTransactionPayload";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import { Toast } from "../../components/Toast";
import "../../api/openApiConfig";

type NotificationType =
  | "transaction"
  | "singleTransaction"
  | "reward"
  | "downsize"
  | "custom";

const notificationTypeOptions: { value: NotificationType; label: string }[] = [
  { value: "transaction", label: "Transaction Notification" },
  { value: "singleTransaction", label: "Single Transaction Notification" },
  { value: "reward", label: "Reward Notification" },
  { value: "downsize", label: "Downsize Notification" },
  { value: "custom", label: "Custom Notification" },
];

const CustomPushTab: React.FC = () => {
  // Business categories
  const [categories, setCategories] = useState<BusinessCategory[]>([]);

  useEffect(() => {
    listBusinessCategories()
      .then(setCategories)
      .catch((err) => console.error("Error fetching business categories:", err));
  }, []);

  // Merchants, for previewing a notification with a real name and a real logo.
  const [businesses, setBusinesses] = useState<BusinessName[]>([]);

  useEffect(() => {
    listBusinessNames()
      .then(setBusinesses)
      .catch((err) => console.error("Error fetching businesses:", err));
  }, []);

  // Users
  const [users, setUsers] = useState<string[]>([]);

  // Notification type
  const [notificationType, setNotificationType] =
    useState<NotificationType>("transaction");

  // Transaction fields
  const [transactionAmount, setTransactionAmount] = useState("");

  // Single Transaction fields
  const [singleTransactionAmount, setSingleTransactionAmount] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [businessContentfulId, setBusinessContentfulId] = useState("");
  const [imageVariant, setImageVariant] = useState<NotificationImageVariant>(
    NotificationImageVariant.LOGO
  );
  const [singleTransactionImageUrl, setSingleTransactionImageUrl] = useState("");

  // Reward fields
  const [rewardAmount, setRewardAmount] = useState("");

  // Downsize fields
  const [downsizeAmount, setDownsizeAmount] = useState("");

  // Custom fields
  const [customTitle, setCustomTitle] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [customImage, setCustomImage] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [customPayload, setCustomPayload] = useState("");
  const [customCtaText, setCustomCtaText] = useState("");
  const [customType] = useState<CustomNotificationType>(
    CustomNotificationType.FIREBASE_CAMPAIGN
  );

  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Clear CTA text when URL is emptied
  useEffect(() => {
    if (!customUrl) {
      setCustomCtaText("");
    }
  }, [customUrl]);

  const singleTransactionForm = {
    amount: singleTransactionAmount,
    businessContentfulId,
    businessName,
    businessCategory,
    imageVariant,
    imageUrl: singleTransactionImageUrl,
  };

  const selectedBusiness = businesses.find((b) => b.id === businessContentfulId);
  const previewImageUrl = resolvePreviewImageUrl(
    singleTransactionForm,
    selectedBusiness
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (users.length === 0) {
      setError("Please add at least one user.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await NotificationService.postApiNotificationSendCustomNotification({
        users,
        transactionNotification:
          notificationType === "transaction"
            ? { amount: Number(transactionAmount) }
            : undefined,
        singleTransactionNotification:
          notificationType === "singleTransaction"
            ? buildSingleTransactionPayload(singleTransactionForm)
            : undefined,
        rewardNotification:
          notificationType === "reward"
            ? { amount: Number(rewardAmount) }
            : undefined,
        downsizeNotification:
          notificationType === "downsize"
            ? { amount: Number(downsizeAmount) }
            : undefined,
        customNotification:
          notificationType === "custom"
            ? {
                title: customTitle || undefined,
                body: customBody || undefined,
                image: customImage || undefined,
                url: customUrl || undefined,
                payload: customPayload || undefined,
                ctaText: customCtaText || undefined,
                type: customType,
              }
            : undefined,
      });

      setToast("Notification sent successfully!");
    } catch (err: any) {
      console.error("Error sending notification:", err);
      setError(
        err?.body?.message || err?.message || "Failed to send notification."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast}
          type="success"
          onClose={() => setToast(null)}
        />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6">
        <h3 className="text-lg font-medium mb-4">Send Custom Notification</h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Users */}
          <div>
            <label className="mb-0.5 block text-sm">Users</label>
            <CreatableSelect
              isMulti
              placeholder="Type user email/ID and press Enter..."
              value={users.map((u) => ({ label: u, value: u }))}
              onChange={(newValue) =>
                setUsers(newValue.map((v) => v.value))
              }
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: "1rem",
                  borderColor: "#d1d5db",
                  minHeight: "3rem",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#9ca3af" },
                }),
                multiValue: (base) => ({
                  ...base,
                  borderRadius: "0.5rem",
                  backgroundColor: "#f3f0f7",
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#503595",
                }),
              }}
            />
            <span className="mt-1 block text-xs text-gray-400">
              Accepts user email addresses or user IDs.
            </span>
          </div>

          {/* Notification Type */}
          <div>
            <label className="mb-0.5 block text-sm">Notification Type</label>
            <select
              value={notificationType}
              onChange={(e) =>
                setNotificationType(e.target.value as NotificationType)
              }
              className="flex w-full rounded-2xl border border-gray-300 bg-transparent px-4 py-[0.8438rem] text-sm leading-none focus-visible:outline-none focus-visible:ring-0"
            >
              {notificationTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Payload Fields */}
          {notificationType === "transaction" && (
            <TextField
              label="Amount"
              type="number"
              placeholder="Enter amount"
              value={transactionAmount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTransactionAmount(e.target.value)
              }
            />
          )}

          {notificationType === "singleTransaction" && (
            <div className="space-y-4">
              <TextField
                label="Amount"
                type="number"
                placeholder="Enter amount"
                value={singleTransactionAmount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSingleTransactionAmount(e.target.value)
                }
              />
              <div>
                <label className="mb-0.5 block text-sm">
                  Merchant{" "}
                  <span className="text-gray-400">(optional)</span>
                </label>
                <Select
                  isClearable
                  placeholder="Search merchants..."
                  options={businesses.map((b) => ({
                    value: b.id,
                    label: b.name,
                  }))}
                  value={
                    selectedBusiness
                      ? {
                          value: selectedBusiness.id,
                          label: selectedBusiness.name,
                        }
                      : null
                  }
                  onChange={(option) =>
                    setBusinessContentfulId(option?.value ?? "")
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "1rem",
                      borderColor: "#d1d5db",
                      minHeight: "3rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#9ca3af" },
                    }),
                  }}
                />
                <span className="mt-1 block text-xs text-gray-400">
                  Picks up the merchant's real name, category and logo from
                  Contentful, so the notification matches what a customer gets.
                </span>
              </div>

              <TextField
                label={
                  <span>
                    Business Name{" "}
                    <span className="text-gray-400 font-normal">
                      (optional override)
                    </span>
                  </span>
                }
                placeholder={
                  selectedBusiness
                    ? `Defaults to "${selectedBusiness.name}"`
                    : "Enter business name"
                }
                value={businessName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setBusinessName(e.target.value)
                }
              />
              <div>
                <label className="mb-0.5 block text-sm">
                  Business Category{" "}
                  <span className="text-gray-400">(optional override)</span>
                </label>
                <select
                  value={businessCategory}
                  onChange={(e) => setBusinessCategory(e.target.value)}
                  className="flex w-full rounded-2xl border border-gray-300 bg-transparent px-4 py-[0.8438rem] text-sm leading-none focus-visible:outline-none focus-visible:ring-0"
                >
                  <option value="">
                    {selectedBusiness
                      ? "Use the merchant's own category"
                      : "Select a category"}
                  </option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-gray-400">
                  Only shown on the legacy notification, which prefixes the
                  message with this category's emoji. The redesigned one has no
                  category emoji.
                </span>
              </div>

              {businessContentfulId && (
                <div>
                  <label className="mb-0.5 block text-sm">Image</label>
                  <select
                    value={imageVariant}
                    onChange={(e) =>
                      setImageVariant(e.target.value as NotificationImageVariant)
                    }
                    className="flex w-full rounded-2xl border border-gray-300 bg-transparent px-4 py-[0.8438rem] text-sm leading-none focus-visible:outline-none focus-visible:ring-0"
                  >
                    {imageVariantOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <TextField
                  label={
                    <span>
                      Image URL{" "}
                      <span className="text-gray-400 font-normal">
                        (optional override)
                      </span>
                    </span>
                  }
                  placeholder="https://example.com/image.png"
                  value={singleTransactionImageUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSingleTransactionImageUrl(e.target.value)
                  }
                />
                <span className="mt-1 block text-xs text-gray-400">
                  Sent verbatim, overriding the merchant's image. Must be a
                  publicly reachable https JPEG, PNG or GIF under 1 MB — SVG does
                  not render on either platform.
                </span>
              </div>

              <div className="rounded-2xl border border-gray-200 p-4">
                <span className="mb-2 block text-sm">Image preview</span>
                {previewImageUrl ? (
                  <img
                    src={previewImageUrl}
                    alt="Notification"
                    className="h-20 w-20 rounded-lg border border-gray-200 object-contain"
                  />
                ) : (
                  <span className="text-xs text-gray-400">
                    {imageVariant === NotificationImageVariant.FIRST_IMAGE &&
                    businessContentfulId
                      ? "No preview available for the first gallery image — it is still sent."
                      : "No image will be sent."}
                  </span>
                )}
              </div>

              <div className="rounded-2xl bg-gray-50 px-4 py-3 text-xs text-gray-500">
                The image and the reworded copy only reach identities on the
                pilot allowlist
                (<code>PurchaseNotificationRedesignAllowedIdentityIds</code>).
                Anyone else on this send receives today's notification, so a
                mixed list shows both variants side by side.
              </div>
            </div>
          )}

          {notificationType === "reward" && (
            <TextField
              label="Amount"
              type="number"
              placeholder="Enter amount"
              value={rewardAmount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRewardAmount(e.target.value)
              }
            />
          )}

          {notificationType === "downsize" && (
            <TextField
              label="Amount"
              type="number"
              placeholder="Enter amount"
              value={downsizeAmount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDownsizeAmount(e.target.value)
              }
            />
          )}

          {notificationType === "custom" && (
            <div className="space-y-4">
              <TextField
                label="Title"
                placeholder="Notification title"
                value={customTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCustomTitle(e.target.value)
                }
              />
              <TextField
                as="textarea"
                label="Body"
                placeholder="Notification body"
                rows={4}
                value={customBody}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setCustomBody(e.target.value)
                }
              />
              <div>
                <TextField
                  label={
                    <span>
                      Image URL{" "}
                      <span className="text-gray-400 font-normal">
                        (optional)
                      </span>
                    </span>
                  }
                  placeholder="https://example.com/image.png"
                  value={customImage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCustomImage(e.target.value)
                  }
                />
                <span className="mt-1 block text-xs text-gray-400">
                  Should be a square image, ideally 80x80 px.
                </span>
              </div>
              <div>
                <TextField
                  label={
                    <span>
                      URL{" "}
                      <span className="text-gray-400 font-normal">
                        (optional)
                      </span>
                    </span>
                  }
                  placeholder="https://example.com or /in-app-path"
                  value={customUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCustomUrl(e.target.value)
                  }
                />
                <span className="mt-1 block text-xs text-gray-400">
                  Supported formats:
                  <br />• Internal URL (starting from {process.env.REACT_APP_FRONTEND_BASE_URL})
                  <br />• Internal path (e.g. /some/path)
                  <br />• Scheme deep link (e.g. oisterbonus://internal/path)
                  <br />• HTTPS deep link (e.g. https://oisterbonus.app.link/internal/path)
                </span>
              </div>
              <div>
                <TextField
                  label={
                    <span>
                      Payload{" "}
                      <span className="text-gray-400 font-normal">
                        (optional — legacy deeplink field)
                      </span>
                    </span>
                  }
                  placeholder="Legacy deeplink payload"
                  value={customPayload}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCustomPayload(e.target.value)
                  }
                />
              </div>
              <div>
                <TextField
                  label={
                    <span>
                      CTA Text{" "}
                      <span className="text-gray-400 font-normal">
                        (optional — defaults to "Ga")
                      </span>
                    </span>
                  }
                  placeholder="Call to action text"
                  value={customCtaText}
                  disabled={!customUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCustomCtaText(e.target.value)
                  }
                />
                <span className="mt-1 block text-xs text-gray-400">
                  Only applies when a URL is set.
                </span>
              </div>
            </div>
          )}

          {/* Submit */}
          <Button type="submit" isLoading={loading} size="lg">
            Send Notification
          </Button>
        </form>
      </div>
    </>
  );
};

export default CustomPushTab;
